import React from "react";

import downArrow from "../../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../../assets/images/svg/mab-ellipse.svg";
import videoFrame from "../../assets/images/svg/video-frame.svg";
import videoMobileFrame from "../../assets/images/svg/video-frame-mobile.svg";
import warningIcon from "../../assets/images/svg/warning.svg";
import NextButton from "../common/next-button";
import StepNumberBadge from "../common/StepNumberBadge";

type VideoItem = {
  id: string;
  fileName: string;
  file?: File;
  isUploading: boolean;
  uploadProgress: number;
  facebookVideoId?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
};

type VideoDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onNext?: (id: string) => void;
  videos: VideoItem[];
  onVideoUploadChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveVideo: (videoId: string) => void;
  isEditMode?: boolean;
  selectedVideoId?: string | null;
  onVideoSelect?: (videoId: string) => void;
  onPreviewScreen?: () => void;
};

const VideoDropdown: React.FC<VideoDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  onNext,
  videos,
  onVideoUploadChange,
  onRemoveVideo,
  isEditMode = false,
  selectedVideoId,
  onVideoSelect,
  onPreviewScreen,
}) => {
  const isOpen = expandedId === id;
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const dragCounterRef = React.useRef(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [sizeValidationError, setSizeValidationError] = React.useState(false);
  const MAX_VIDEOS = 5;
  const MAX_VIDEO_SIZE = 150 * 1024 * 1024; // 150MB in bytes
  const hasVideos = videos.length > 0;
  const canAddMore = videos.length < MAX_VIDEOS;
  const isAnyUploading = videos.some((v) => v.isUploading);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only process if files are actually selected
    if (e.target.files && e.target.files.length > 0) {
      // Check file size validation (150MB max)
      const files = Array.from(e.target.files);
      const hasInvalidSize = files.some(file => file.size > MAX_VIDEO_SIZE);

      if (hasInvalidSize) {
        setSizeValidationError(true);
        return;
      }

      setSizeValidationError(false);
      onVideoUploadChange(e);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleVideoClick = (videoId: string) => {
    if (onVideoSelect && !isAnyUploading) {
      onVideoSelect(videoId);
    }
  };

  const handleUploadClick = () => {
    setSizeValidationError(false);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAddMore && !isEditMode) {
      dragCounterRef.current++;
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAddMore && !isEditMode) {
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    if (!canAddMore || isEditMode) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Check file size validation (150MB max)
      const filesArray = Array.from(files);
      const hasInvalidSize = filesArray.some(file => file.size > MAX_VIDEO_SIZE);

      if (hasInvalidSize) {
        setSizeValidationError(true);
        return;
      }

      setSizeValidationError(false);
      // Create a synthetic event with the dropped files
      const syntheticEvent = {
        target: { files },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      onVideoUploadChange(syntheticEvent);
    }
  };

  return (
    <div className="w-full">
      <div
        className="relative rounded-[12px] sm:rounded-[16px] md:rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] overflow-hidden w-full"
        style={{
          background: "#878DA11A",
          backgroundImage: `url(${ellipseBg})`,
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-90 transition-opacity min-h-[60px] h-[60px] sm:min-h-[72px] sm:h-[72px] md:min-h-[88px] md:h-[88px] px-4 py-4 sm:px-4 sm:py-4 md:px-6 md:py-6"
          onClick={() => onToggle(id)}
        >
          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
            <StepNumberBadge stepNumber={5} isOpen={isOpen} />
            <span
              className="text-[16px] lg:text-[20px] whitespace-nowrap"
              style={{
                fontWeight: 400,
                lineHeight: "130%",
                color: isOpen ? "#58F2A9" : "#FFFFFF",
              }}
            >
              Ad Creative
            </span>
          </div>
          <img
            src={downArrow}
            alt="Toggle"
            className="pointer-events-none transition-transform flex-shrink-0"
            style={{
              height: "9.5px",
              width: "auto",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              filter: isOpen
                ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
                : "none",
            }}
          />
        </div>

        {isOpen && (
          <div className="flex flex-col gap-3 sm:gap-4 md:gap-6 px-4 pb-4 sm:px-4 sm:pb-4 md:px-6 md:pb-6 md:ml-[41px]">
            <div className="flex flex-col gap-2">
              <span
                className="text-[14px] lg:text-[16px] font-normal leading-[130%]"
                style={{ color: "#E9EEF5" }}
              >
                Upload your ad video(s)!
              </span>
              <span className="text-[#8FA5BF] text-[12px] lg:text-[14px] font-medium leading-[150%]">
                Upload up to {MAX_VIDEOS} videos (15 - 60 seconds) here.{" "}
                {videos.length > 0 &&
                  `(${videos.length}/${MAX_VIDEOS} uploaded)`}
              </span>
            </div>

            {/* Drop zone - show when can add more videos */}
            {canAddMore && !isEditMode && (
              <>
                {/* Mobile frame - shown only on small screens */}
                <label
                  htmlFor="video-upload"
                  className="relative w-full flex flex-col items-center justify-center cursor-pointer rounded-[14px] md:hidden mb-3"
                  style={{
                    minHeight: "200px",

                    backgroundImage: `url(${videoMobileFrame})`,
                    backgroundSize: "100% 100%",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                  }}
                  onClick={handleUploadClick}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {isDragging && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[rgba(88,242,169,0.15)] rounded-[14px] pointer-events-none">
                      <div className="text-center px-2">
                        <div className="text-[#58F2A9] text-base font-semibold mb-2 font-[Poppins]">
                          Drop your videos here
                        </div>
                        <div className="text-[#58F2A9] text-sm font-[Poppins]">
                          Release to upload
                        </div>
                      </div>
                    </div>
                  )}
                </label>
                {/* Desktop frame - shown on medium and larger screens */}
                <label
                  htmlFor="video-upload"
                  className="relative w-full min-h-[200px] mt-[-40px] lg:mt-[-2px] md:min-h-[260px] hidden md:flex flex-col items-center justify-center cursor-pointer rounded-[14px] transition-all duration-200 overflow-hidden"
                  style={{
                    backgroundImage: `url(${videoFrame})`,
                    backgroundSize: "100% 100%",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                  }}
                  onClick={handleUploadClick}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {isDragging && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[rgba(88,242,169,0.15)] rounded-[14px] pointer-events-none">
                      <div className="text-center px-2">
                        <div className="text-[#58F2A9] text-base md:text-lg font-semibold mb-2 font-[Poppins]">
                          Drop your videos here
                        </div>
                        <div className="text-[#58F2A9] text-sm font-[Poppins]">
                          Release to upload
                        </div>
                      </div>
                    </div>
                  )}
                </label>
              </>
            )}

            <input
              ref={fileInputRef}
              id="video-upload"
              type="file"
              accept=".mp4,video/mp4,video/*"
              multiple
              className="hidden"
              onChange={handleFileChange}
              disabled={!canAddMore || isEditMode}
            />

            {/* Size validation error message */}
            {sizeValidationError && (
              <div className="flex items-center gap-2 text-[#EE404C] text-[13px] font-normal pt-1">
                <img src={warningIcon} alt="" className="w-[16px] h-[16px]" />
                <span>
                  The video file is too large. The maximum allowed size is 150 MB
                </span>
              </div>
            )}

            {/* Uploaded videos list */}
            {hasVideos && (
              <div className="flex flex-col gap-3">
                {videos.map((video) => (
                  <div
                    key={video.id}
                    className="w-full rounded-[14px] bg-[#121420] border border-[#2A2F3D] p-3 flex flex-col gap-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-white text-sm font-medium [font-family:'Poppins',Helvetica]">
                        {video.isUploading ? "Uploading..." : "Uploaded video"}
                      </div>
                      {!isEditMode && (
                        <button
                          type="button"
                          onClick={() => onRemoveVideo(video.id)}
                          className="text-xs text-[#EE6161] hover:underline font-medium"
                          disabled={video.isUploading}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {/* Progress bar - only show when uploading */}
                    {video.isUploading && (
                      <div className="w-full h-2 rounded-full bg-[#1E2433] overflow-hidden">
                        <div
                          className="h-full bg-[#58F2A9] transition-all duration-200"
                          style={{ width: `${video.uploadProgress}%` }}
                        />
                      </div>
                    )}

                    {/* Success message - only show when upload is complete */}
                    {!video.isUploading && (
                      <span className="text-[#58F2A9] text-xs [font-family:'Poppins',Helvetica]">
                        ✓ Video uploaded successfully!
                      </span>
                    )}

                    <div className="text-[#878DA1] text-xs [font-family:'Poppins',Helvetica] break-all">
                      {video.fileName}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex w-full justify-end">
              <NextButton
                size="sm"
                className="w-full md:w-auto"
                onClick={() => onNext && onNext(id)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoDropdown;
